import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cancelBankDeposit,
  cancelClosing,
  createBankDeposit,
  createCashRegister,
  createClosing,
  deleteCashRegister,
  fetchBankDepositDetail,
  fetchBankDeposits,
  fetchCashRegisters,
  fetchClosingDetail,
  fetchClosings,
  fetchRegisterCurrent,
  fetchSafe,
  updateCashRegister,
  type CreateBankDepositResponse,
  type CreateClosingResponse,
} from '../api/cashFlow';
import type {
  BankDepositDetail,
  BankDepositSummary,
  CashRegister,
  CashRegisterUpsert,
  ClosingDetail,
  ClosingSummary,
  CreateBankDepositRequest,
  CreateClosingRequest,
  ListBankDepositsQuery,
  ListClosingsQuery,
  RegisterCurrent,
  SafeState,
} from '@deposits/shared';

// ───────────────────────────── Cash registers ──────────────────────────────

export function useCashRegisters(locationId: number | null, projectId: number | null) {
  return useQuery<CashRegister[]>({
    queryKey: ['cash-registers', locationId, projectId],
    queryFn: () => fetchCashRegisters(locationId ?? undefined, projectId),
    enabled: locationId != null,
    staleTime: 30_000,
  });
}

/** Live drawer state. Not cached long — it reflects Arbox in real time. */
export function useRegisterCurrent(id: number | null) {
  return useQuery<RegisterCurrent>({
    queryKey: ['register-current', id],
    queryFn: () => fetchRegisterCurrent(id!),
    enabled: id != null,
    staleTime: 0,
  });
}

export function useSaveCashRegister() {
  const qc = useQueryClient();
  return useMutation<CashRegister, Error, { id?: number; body: CashRegisterUpsert }>({
    mutationFn: ({ id, body }) =>
      id != null ? updateCashRegister(id, body) : createCashRegister(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cash-registers'] }),
  });
}

export function useDeleteCashRegister() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: deleteCashRegister,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cash-registers'] }),
  });
}

// ─────────────────────────── Register closings ──────────────────────────────

export function useClosings(params: ListClosingsQuery | null) {
  return useQuery<ClosingSummary[]>({
    queryKey: ['closings', params],
    queryFn: () => fetchClosings(params!),
    enabled: params != null,
    staleTime: 15_000,
  });
}

export function useClosingDetail(id: number | null) {
  return useQuery<ClosingDetail>({
    queryKey: ['closing-detail', id],
    queryFn: () => fetchClosingDetail(id!),
    enabled: id != null,
    staleTime: 15_000,
  });
}

export function useCreateClosing() {
  const qc = useQueryClient();
  return useMutation<CreateClosingResponse, Error, CreateClosingRequest>({
    mutationFn: createClosing,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['closings'] });
      qc.invalidateQueries({ queryKey: ['register-current'] });
      qc.invalidateQueries({ queryKey: ['safe'] });
      qc.invalidateQueries({ queryKey: ['cash-registers'] });
    },
  });
}

export function useCancelClosing() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: number; reason?: string | null }>({
    mutationFn: ({ id, reason }) => cancelClosing(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['closings'] });
      qc.invalidateQueries({ queryKey: ['register-current'] });
      qc.invalidateQueries({ queryKey: ['safe'] });
    },
  });
}

// ─────────────────────────── Bank deposits (safe) ───────────────────────────

export function useSafe(locationId: number | null, projectId: number | null) {
  return useQuery<SafeState>({
    queryKey: ['safe', locationId, projectId],
    queryFn: () => fetchSafe(locationId!, projectId),
    enabled: locationId != null,
    staleTime: 15_000,
  });
}

export function useBankDeposits(params: ListBankDepositsQuery | null) {
  return useQuery<BankDepositSummary[]>({
    queryKey: ['bank-deposits', params],
    queryFn: () => fetchBankDeposits(params!),
    enabled: params != null,
    staleTime: 15_000,
  });
}

export function useBankDepositDetail(id: number | null) {
  return useQuery<BankDepositDetail>({
    queryKey: ['bank-deposit-detail', id],
    queryFn: () => fetchBankDepositDetail(id!),
    enabled: id != null,
    staleTime: 15_000,
  });
}

export function useCreateBankDeposit() {
  const qc = useQueryClient();
  return useMutation<CreateBankDepositResponse, Error, CreateBankDepositRequest>({
    mutationFn: createBankDeposit,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['safe'] });
      qc.invalidateQueries({ queryKey: ['bank-deposits'] });
      qc.invalidateQueries({ queryKey: ['closings'] });
    },
  });
}

export function useCancelBankDeposit() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: number; reason?: string | null }>({
    mutationFn: ({ id, reason }) => cancelBankDeposit(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['safe'] });
      qc.invalidateQueries({ queryKey: ['bank-deposits'] });
      qc.invalidateQueries({ queryKey: ['closings'] });
    },
  });
}
